import { useTranslation } from "react-i18next";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { SPOKEN_LANGUAGES } from "../lib/spoken-languages";

/**
 * Les DEUX langues d'un collaborateur, côte à côte.
 *
 * Elles se ressemblent et ne commandent pas la même chose : les séparer sur les écrans qui
 * les saisissent (wizard de création, fiche collaborateur, « Mon profil ») reviendrait à
 * recopier l'explication qui les distingue — et à la laisser diverger. D'où ce composant
 * unique, monté partout où l'on saisit la langue d'un collaborateur.
 *
 * `spokenLanguage = null` signifie « pas de langue d'entraînement distincte » : le
 * collaborateur s'entraîne dans sa langue d'affichage. C'est le cas courant, donc la valeur
 * par défaut — et il faut qu'il reste lisible à l'écran, d'où l'option explicite plutôt
 * qu'un champ vide.
 */

/** Valeur de l'option « identique à la langue d'affichage ». Radix Select interdit `""`. */
const SAME_AS_DISPLAY = "__same__";

interface CollaboratorLanguageFieldsProps {
  language: string | null | undefined;
  spokenLanguage: string | null | undefined;
  onChange: (field: "language" | "spokenLanguage", value: string | null) => void;
  /** `grid` = côte à côte ; `stack` = l'une sous l'autre (mobile). */
  layout?: "grid" | "stack";
  /**
   * Nombre de colonnes de la grille du formulaire HÔTE, pour que les deux champs tombent
   * dans ses gouttières. Le wizard de création aligne sur 2 colonnes, la fiche d'édition
   * sur 3 (Civilité · Prénom · Nom) : sans ce réglage, les langues débordaient d'une
   * demi-colonne et ne s'alignaient sur aucun champ au-dessus.
   */
  columns?: 2 | 3;
  /** Hauteur des champs, pour s'aligner sur le formulaire hôte. */
  inputClassName?: string;
  /**
   * Verrouille la langue d'ENTRAÎNEMENT seule. « Mon profil » n'ouvre ses champs qu'en mode
   * édition, alors que la langue d'affichage y bascule l'interface immédiatement, à tout
   * moment : les deux champs n'y suivent pas la même règle.
   */
  spokenLanguageDisabled?: boolean;
}

export function CollaboratorLanguageFields({
  language,
  spokenLanguage,
  onChange,
  layout = "grid",
  columns = 2,
  inputClassName = "",
  spokenLanguageDisabled = false,
}: CollaboratorLanguageFieldsProps) {
  const { t } = useTranslation();

  // Gouttière reprise du formulaire hôte : gap-4 sur 3 colonnes, gap-5 sur 2.
  //
  // UNE colonne sous 640 px, quoi qu'en dise l'hôte. Les deux libellés et la valeur
  // « Identique à la langue d'affichage » sont longs : sur un téléphone, à deux colonnes,
  // le libellé se coupait en deux lignes et la valeur du select était tronquée à
  // « Identique à la… » — un champ dont on ne peut plus lire ce qu'il contient.
  const gridClassName = columns === 3
    ? "grid grid-cols-1 gap-4 sm:grid-cols-3"
    : "grid grid-cols-1 gap-5 sm:grid-cols-2";

  return (
    <div className="space-y-2">
      <div className={layout === "grid" ? `${gridClassName} items-start` : "space-y-4"}>
        {/* Chaque explication reste SOUS son propre champ : mises bout à bout dans un même
            paragraphe, les deux se lisaient comme une seule phrase et la seconde semblait
            commencer sous le premier champ. */}
        <div className="space-y-2">
          <Label className="text-sm font-medium">{t("account.displayLanguage")}</Label>
          <Select
            value={language || "fr"}
            onValueChange={(value) => onChange("language", value)}
          >
            <SelectTrigger className={`w-full ${inputClassName}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="fr" className="text-base">{t("language.fr")}</SelectItem>
              <SelectItem value="en" className="text-base">{t("language.en")}</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground leading-relaxed">
            {t("account.displayLanguageHelp")}
          </p>
        </div>

        <div className="space-y-2">
          <Label className="text-sm font-medium">{t("account.spokenLanguage")}</Label>
          <Select
            value={spokenLanguage || SAME_AS_DISPLAY}
            onValueChange={(value) => onChange("spokenLanguage", value === SAME_AS_DISPLAY ? null : value)}
            disabled={spokenLanguageDisabled}
          >
            <SelectTrigger className={`w-full ${inputClassName}`}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              <SelectItem value={SAME_AS_DISPLAY} className="text-base">
                {t("account.spokenLanguageSame")}
              </SelectItem>
              {SPOKEN_LANGUAGES.map((lang) => (
                // Nom NATIF suivi du nom ANGLAIS : « Magyar (Hungarian) ».
                //
                // Le nom natif seul suppose que celui qui saisit reconnaisse « Magyar »,
                // « Hrvatski » ou « Հայերեն » — ce qui n'a rien d'évident pour un
                // francophone qui crée le compte de quelqu'un d'autre. Le nom anglais
                // lève le doute sans imposer de traduire 33 noms de langues en FR et EN.
                <SelectItem key={lang.code} value={lang.code} className="text-base">
                  {lang.nativeName === lang.englishName
                    ? lang.nativeName
                    : `${lang.nativeName} (${lang.englishName})`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground leading-relaxed">
            {t("account.spokenLanguageHelp")}
          </p>
        </div>
      </div>

      {/* La limite vaut pour les DEUX champs : elle reste pleine largeur, sous l'ensemble. */}
      <p className="text-xs text-muted-foreground leading-relaxed">
        {t("account.languagesNote")}
      </p>
    </div>
  );
}
